import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { TIDS } from "@/constants/testIds";
import TicTacToe from "./TicTacToe";
import Connect4 from "./Connect4";
import RPS from "./RPS";
import Hangman from "./Hangman";
import WordScramble from "./WordScramble";
import DiceRoll from "./DiceRoll";
import CoinFlip from "./CoinFlip";
import Checkers from "./Checkers";
import Slots from "./Slots";
import ChessGame from "./ChessGame";
import Holdem from "./Holdem";
import Pinball from "./Pinball";
import OrbDrop from "./OrbDrop";
import PaddleClash from "./PaddleClash";
import NeonCycles from "./NeonCycles";
import { Lock, Gamepad2, X, Sparkles, ExternalLink } from "lucide-react";
import useGameTick from "@/lib/useGameTick";

export default function GameSlot({ ws, state, isStreamer, channelUsername }) {
  const [catalog, setCatalog] = useState([]);
  const slug = state?.slug;
  const navigate = useNavigate();

  // Award 1 Spark/min for time played inside a streamer's channel (also progresses community XP)
  useGameTick(slug, channelUsername);

  useEffect(() => {
    api.get("/games/catalog").then(r => setCatalog(r.data));
  }, []);

  const sendState = (data) => {
    if (!ws || ws.readyState !== 1) return;
    ws.send(JSON.stringify({ type: "game_move", state: { slug, data } }));
  };
  const selectGame = (newSlug) => {
    // Games with an external full-screen route (e.g. Neon Serpent) navigate away
    // instead of embedding into the in-channel slot.
    const meta = catalog.find(g => g.slug === newSlug);
    if (meta?.external_route) {
      navigate(meta.external_route);
      return;
    }
    if (!ws || ws.readyState !== 1) return;
    ws.send(JSON.stringify({ type: "game_select", slug: newSlug }));
  };
  const closeGame = () => {
    if (!ws || ws.readyState !== 1) return;
    ws.send(JSON.stringify({ type: "game_select", slug: null }));
  };

  const renderGame = () => {
    switch (slug) {
      case "ttt": return <TicTacToe state={state?.data} sendState={sendState} />;
      case "connect4": return <Connect4 state={state?.data} sendState={sendState} />;
      case "rps": return <RPS />;
      case "hangman": return <Hangman />;
      case "wordscramble": return <WordScramble />;
      case "diceroll": return <DiceRoll />;
      case "coinflip": return <CoinFlip />;
      case "slots": return <Slots />;
      case "checkers": return <Checkers />;
      case "chess": return <ChessGame state={state?.data} sendState={(d) => {
        if (!ws || ws.readyState !== 1) return;
        ws.send(JSON.stringify({ type: "chess_move", state: d }));
      }} />;
      case "holdem": return <Holdem ws={ws} isStreamer={isStreamer} channelUsername={channelUsername} />;
      case "pinball": return <Pinball />;
      case "orbdrop": return <OrbDrop ws={ws} isStreamer={isStreamer} channelUsername={channelUsername} />;
      case "pong": return <PaddleClash />;
      case "tron": return <NeonCycles />;
      case "neon-serpent":
        // Standalone full-screen game — offer a launch button rather than embed.
        return (
          <div className="p-6 text-center border border-white/10 rounded-md bg-black/30" data-testid="game-slot-neon-serpent-launch">
            <Gamepad2 className="w-10 h-10 mx-auto mb-3" style={{ color: "var(--sc-cyan)" }} />
            <div className="sc-heading text-lg font-bold">Neon Serpent</div>
            <div className="text-xs text-zinc-400 mt-1 mb-4">Full-screen multiplayer arena. Opens in the arcade view.</div>
            <button
              type="button"
              onClick={() => navigate("/games/neon-serpent")}
              className="sc-btn text-sm inline-flex items-center gap-2"
              data-testid="game-slot-neon-serpent-open"
            >
              <ExternalLink className="w-3.5 h-3.5" /> Launch Neon Serpent
            </button>
          </div>
        );
      default: return null;
    }
  };

  const available = catalog.filter(g => g.available);
  const soon = catalog.filter(g => !g.available);

  return (
    <div className="bg-[#18181B] border border-white/10 rounded-md overflow-hidden" data-testid="game-slot">
      <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Gamepad2 className="w-4 h-4 text-[#FF3366]" />
          <div className="sc-heading text-base font-bold">In-Stream Game</div>
          {slug && <span className="ml-2 text-[10px] tracking-widest uppercase font-bold text-[#10B981]">Active</span>}
        </div>
        <div className="flex items-center gap-2">
          {slug && isStreamer && (
            <button data-testid="game-close-button" onClick={closeGame} className="sc-btn-ghost text-xs flex items-center gap-1.5">
              <X className="w-3.5 h-3.5" /> End Game
            </button>
          )}
          {isStreamer && (
            <select
              data-testid={TIDS.channelGameSelect}
              value={slug || ""}
              onChange={(e) => e.target.value ? selectGame(e.target.value) : closeGame()}
              className="sc-input max-w-xs text-sm"
            >
              <option value="">— Pick a game —</option>
              {available.map(g => (
                <option key={g.slug} value={g.slug}>{`${g.name} (${g.players})`}</option>
              ))}
              <optgroup label="Coming Soon">
                {soon.map(g => (
                  <option key={g.slug} value="" disabled>{`${g.name} — Soon`}</option>
                ))}
              </optgroup>
            </select>
          )}
        </div>
      </div>

      <div className="p-5">
        {slug ? (
          renderGame()
        ) : isStreamer ? (
          <div>
            <div className="flex items-start gap-3 p-4 mb-5 border border-[#FF3366]/30 bg-[#FF3366]/5 rounded-sm">
              <Sparkles className="w-5 h-5 text-[#FF3366] flex-shrink-0 mt-0.5" />
              <div>
                <div className="text-sm font-bold">You're the host — open a game for your viewers</div>
                <div className="text-xs text-zinc-400 mt-1">Pick a game below. It launches live in the room and the whole chat can play with you in real time.</div>
              </div>
            </div>
            <div className="sc-label mb-3 text-[#10B981]">Playable Now</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-6">
              {available.map((g) => (
                <button
                  key={g.slug}
                  data-testid={`game-tile-${g.slug}`}
                  onClick={() => selectGame(g.slug)}
                  className="p-4 text-left border border-white/10 hover:border-[#FF3366]/60 cursor-pointer bg-black/30 rounded-sm transition group"
                >
                  <div className="text-[10px] tracking-widest uppercase text-[#00E5FF]">{g.category}</div>
                  <div className="sc-heading text-base font-bold mt-2 group-hover:text-[#FF3366] transition">{g.name}</div>
                  <div className="text-xs text-zinc-400 mt-0.5">{g.players} players</div>
                  <div className="text-[11px] mt-3 tracking-widest uppercase font-bold text-[#FF3366]">▶ Launch</div>
                </button>
              ))}
            </div>
            <div className="sc-label mb-3">Coming Soon</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {soon.map((g) => (
                <div key={g.slug} className="relative p-3 border border-white/5 bg-black/20 rounded-sm opacity-60">
                  <div className="text-[10px] tracking-widest uppercase text-zinc-500">{g.category}</div>
                  <div className="text-sm font-semibold mt-1 text-zinc-300">{g.name}</div>
                  <div className="text-[11px] text-zinc-500 mt-0.5">{g.players}</div>
                  <Lock className="absolute top-2 right-2 w-3 h-3 text-zinc-600" />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center py-10 px-6">
            <Gamepad2 className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
            <div className="sc-heading text-lg font-bold text-zinc-300">No game running</div>
            <div className="text-xs text-zinc-500 mt-1">When the streamer launches a game, it'll appear here for everyone to join.</div>
          </div>
        )}
      </div>
    </div>
  );
}
